/**
 * 아이템 아이콘 매핑 — **자동 생성 파일. 손으로 고치지 말 것.**
 *
 * 생성:
 *   python3 scripts/gen_item_icons.py
 *
 * 서버는 `S_InvList`에서 아이템마다 `inv_gfx_id`를 gfxId로 보낸다
 * (jp.l1j.server.packets.server.S_InvList → L1Item.getGfxId → DB inv_gfx_id).
 * 이 값은 원본 클라이언트의 아이콘 스프라이트 번호라서 웹에서 쓸 그림이 없다.
 * gfxId는 2,000개 가까이 되므로 1:1로 그림을 만들 수 없어, DB의 종류 컬럼
 * (weapons.type / armors.type / etc_items.item_type·use_type)을 근거로
 * 1506개의 gfxId를 아래 카테고리로 접었다. 표에 없는 gfxId는 'misc'이 된다.
 *
 * 아이콘 파일은 `web-client/public/assets/gfx/items/{kind}.svg`에 있고,
 * 로드는 캐릭터 스프라이트와 같은 `utils/spriteLoader.ts`의
 * `loadSvgAsTexture()` 파이프라인을 그대로 쓴다 (SVG → base64 → Phaser 텍스처).
 */

import { loadSvgAsTexture } from '../utils/spriteLoader';

export type ItemIconKind =
  'sword' | 'dagger' | 'axe' | 'spear' | 'bow' | 'staff' | 'armor' | 'helm' | 'shield' | 'boots' | 'glove' | 'cloak' | 'ring' | 'amulet' | 'belt' | 'potion' | 'scroll' | 'food' | 'arrow' | 'gem' | 'coin' | 'quest' | 'misc';

/** 전 종류. 프리로드 순회에 쓴다. */
export const ITEM_ICON_KINDS: ItemIconKind[] = ['sword', 'dagger', 'axe', 'spear', 'bow', 'staff', 'armor', 'helm', 'shield', 'boots', 'glove', 'cloak', 'ring', 'amulet', 'belt', 'potion', 'scroll', 'food', 'arrow', 'gem', 'coin', 'quest', 'misc'];

/** 표에 없는 gfxId가 쓰는 종류. */
export const ITEM_ICON_FALLBACK: ItemIconKind = 'misc';

/**
 * 종류별 gfxId 목록 (자동 생성).
 * 'misc'은 기본값이므로 표에 담지 않는다 — 파일만 커지고 결과는 같다.
 */
const ICON_GFX: Partial<Record<ItemIconKind, number[]>> = {
  sword: [
    2, 4, 21, 58, 90, 103, 104, 105, 106, 112, 151, 155, 156, 163, 276, 298, 321, 322, 325,
    438, 441, 463, 897, 902, 932, 1010, 1016, 1019, 1020, 1064, 1067, 1069, 1072, 1078, 1083,
    1085, 1088, 1090, 1145, 1181, 1241, 1243, 1250, 1259, 1260, 1324, 1335, 1341, 1511, 1512,
    1656, 1720, 1778, 1779, 1785, 1871, 1875, 1876, 1893, 1894, 1914, 1917, 1918, 1920, 1945,
    1949, 2160, 2268, 2523, 2525, 2527, 2651, 2750, 2764, 2911, 2917, 3018, 3020, 3022, 3033,
    3047, 3049, 3056, 3133, 3135, 3172, 3233, 3301, 3303, 3709, 3713, 3717, 4681, 4693, 4695,
    4994, 5123, 5125, 5438, 5530, 5532,
  ],
  dagger: [
    33, 34, 35, 144, 1065, 1068, 1070, 1073, 1079, 1084, 1086, 1089, 1091, 1242, 1317, 1332,
    1534, 1629, 1635, 1721, 1795, 1872, 1915, 1921, 1947, 1951, 1995, 2225, 2524, 2530, 2913,
    3139, 3145, 3178, 3715, 4687, 4961,
  ],
  axe: [
    1, 8, 9, 31, 42, 67, 68, 119, 171, 172, 173, 439, 980, 1227, 1627, 1658, 2009, 2213, 2228,
    2509, 2526, 2909, 5534,
  ],
  spear: [
    11, 13, 38, 41, 45, 48, 50, 60, 66, 72, 98, 99, 100, 111, 113, 315, 316, 317, 1228, 1333,
    2200, 2226, 2529, 2758, 2919, 3330,
  ],
  bow: [
    18, 19, 20, 123, 278, 279, 305, 705, 896, 931, 1008, 1018, 1066, 1075, 1080, 1251, 1326,
    1328, 1336, 1513, 1719, 1873, 2005, 2168, 2230, 2531, 2761, 3053, 3141, 3146, 3711, 4683,
    4963,
  ],
  staff: [
    83, 117, 291, 292, 293, 294, 297, 939, 1003, 1239, 1240, 1255, 1257, 1334, 1487, 1516,
    1654, 1878, 1901, 1919, 1953, 2161, 2528, 2756, 2915, 3016, 3051, 3143, 3719, 3737, 4685,
    5549,
  ],
  armor: [
    10, 22, 26, 32, 51, 73, 86, 89, 102, 109, 110, 275, 281, 284, 285, 287, 299, 301, 302, 442,
    443, 448, 460, 510, 527, 539, 540, 541, 542, 695, 696, 898, 899, 900, 901, 930, 956, 960,
    972, 978, 1053, 1077, 1196, 1197, 1198, 1199, 1200, 1201, 1229, 1246, 1256, 1327, 1329,
    1330, 1337, 1486, 1490, 1509, 1519, 1522, 1533, 1630, 1663, 1768, 1793, 1822, 1829, 1895,
    1902, 1909, 1955, 1976, 1980, 2000, 2074, 2075, 2162, 2282, 2620, 2653, 2663, 3058, 3060,
    3062, 3064, 3158, 3170, 3235, 3647, 3648, 3649, 3650, 3651, 3652, 3653, 3654, 3655, 3656,
    3657, 3658, 3659, 3660, 3661, 3662, 5132, 5144,
  ],
  helm: [
    25, 52, 53, 54, 55, 166, 167, 168, 170, 184, 267, 445, 451, 453, 456, 507, 526, 713, 714,
    895, 903, 908, 913, 915, 916, 950, 951, 959, 969, 1001, 1038, 1050, 1071, 1204, 1231, 1232,
    1233, 1234, 1245, 1319, 1420, 1527, 1628, 1641, 1764, 1797, 1866, 1896, 1903, 1993, 1994,
    2163, 2232, 2259, 2466, 2517, 2538, 2625, 3168, 3326, 3609, 3610, 3785, 4061, 5136, 5148,
  ],
  shield: [
    61, 95, 97, 114, 137, 177, 332, 449, 450, 457, 508, 906, 954, 979, 1017, 1202, 1203, 1331,
    1660, 1798, 1874, 1900, 1907, 1973, 2201, 2622, 3006, 3024, 3041, 3164, 3230, 3231, 3328,
    3616, 4187, 4996, 5164, 5166,
  ],
  boots: [
    65, 303, 435, 447, 452, 461, 509, 701, 702, 704, 961, 964, 1044, 1052, 1093, 1230, 1247,
    1258, 1325, 1489, 1525, 1639, 1669, 1766, 1794, 1899, 1906, 1910, 2004, 2130, 2166, 2623,
    2851, 2863, 3154, 4697, 5134, 5146,
  ],
  glove: [
    141, 327, 328, 506, 707, 708, 709, 710, 711, 712, 962, 971, 1011, 1040, 1076, 1095, 1244,
    1526, 1631, 1632, 1633, 1634, 1665, 1765, 1796, 1877, 1898, 1905, 1911, 1967, 1990, 2008,
    2165, 2522, 2624, 2791, 3166, 3324, 4699,
  ],
  cloak: [
    27, 28, 29, 30, 69, 265, 272, 517, 519, 907, 941, 1007, 1074, 1081, 1082, 1087, 1092, 1094,
    1223, 1248, 1249, 1339, 1510, 1514, 1515, 1667, 1718, 1767, 1897, 1904, 1908, 1966, 1979,
    2006, 2164, 2551, 2552, 2562, 2621, 2961, 3004, 3023, 3162, 4706,
  ],
  ring: [
    87, 625, 626, 627, 628, 629, 630, 666, 667, 668, 669, 670, 905, 976, 1029, 1037, 1194,
    1195, 1252, 1253, 1344, 1469, 1496, 1518, 1548, 1549, 1615, 1637, 1770, 1772, 1774, 1776,
    1865, 1981, 1982, 1983, 1984, 1985, 1986, 1987, 1988, 2010, 2017, 2030, 2066, 2114, 2131,
    2132, 2133, 2134, 2135, 2136, 2137, 2138, 2139, 2140, 2173, 2174, 2175, 2176, 2275, 2298,
    2300, 2302, 2304, 2317, 2601, 2664, 2668, 2921, 2978, 2979, 2980, 2983, 3044, 3055, 3237,
    3239, 3240, 3242, 3246, 3248, 3250, 3499, 3501, 3503, 3505, 3507, 3509, 3849, 3853, 3855,
    3857, 3859, 3861, 5078, 5080, 5082, 5084, 5335,
  ],
  amulet: [
    304, 609, 610, 611, 612, 613, 637, 638, 639, 640, 641, 642, 665, 989, 1165, 1184, 1187,
    1188, 1189, 1190, 1191, 1192, 1193, 1254, 1485, 1517, 1521, 1547, 1613, 1769, 1912, 1913,
    2002, 2023, 2024, 2025, 2026, 2028, 2031, 2113, 2172, 2217, 2218, 2219, 2220, 2221, 2222,
    2223, 2224, 2234, 2235, 2271, 2446, 2470, 2506, 2666, 2950, 2973, 3285, 3332, 3334, 3683,
    3684, 3685, 3687, 3755, 3757, 3812, 3922, 4707, 4708, 5337, 5339, 5341,
  ],
  belt: [
    631, 632, 633, 634, 635, 636, 697, 698, 699, 981, 1009, 1168, 1169, 1170, 1916, 2022, 2904,
    2905, 2906, 2907, 2908, 3045, 3474, 3475, 3476, 3477, 3478, 3479, 3480, 3481, 3482, 3483,
    3484, 3485, 3486, 3487, 3596, 3598, 3599, 3601, 3602, 3604, 3605, 4178, 5155, 5157, 5159,
    5527, 5528, 5529,
  ],
  potion: [
    75, 76, 77, 78, 79, 80, 81, 82, 124, 126, 130, 309, 488, 532, 535, 536, 556, 557, 621, 752,
    904, 947, 1032, 1033, 1034, 1139, 1140, 1141, 1142, 1143, 1144, 1157, 1158, 1159, 1160,
    1161, 1162, 1163, 1396, 1397, 1480, 1481, 1482, 1483, 1492, 1529, 1646, 1647, 1730, 1761,
    2310, 2311, 2313, 2315, 2321, 2332, 2335, 2368, 2369, 2370, 2464, 2487, 2488, 2492, 2494,
    2496, 2497, 2503, 2516, 2520, 2521, 2554, 2559, 2602, 2604, 2897, 2900, 2901, 2902, 2984,
    2985, 2988, 2990, 2991, 3038, 3454, 3455, 3456, 3457, 3458, 3459, 3460, 3467, 3469, 3470,
    3471, 3472, 3597, 3600, 3603, 3671, 3723, 3846, 3904, 3905, 4034, 4035,
  ],
  scroll: [
    116, 118, 244, 249, 250, 251, 252, 254, 255, 256, 257, 383, 384, 385, 386, 387, 388, 389,
    390, 391, 392, 393, 394, 395, 396, 397, 398, 399, 400, 401, 402, 403, 404, 405, 406, 407,
    408, 409, 410, 411, 412, 413, 414, 415, 417, 418, 419, 420, 421, 422, 424, 425, 426, 427,
    428, 429, 432, 467, 468, 469, 470, 471, 472, 473, 474, 475, 476, 479, 551, 622, 643, 647,
    649, 651, 653, 655, 657, 659, 661, 663, 673, 674, 675, 676, 677, 678, 679, 680, 681, 682,
    683, 684, 685, 686, 687, 688, 689, 715, 726, 746, 749, 755, 758, 775, 784, 791, 793, 822,
    828, 848, 850, 858, 859, 860, 862, 863, 864, 910, 911, 928, 929, 1035, 1036, 1047, 1206,
    1494, 1570, 1571, 1576, 1577, 1578, 1579, 1582, 1610, 1686, 1687, 1690, 1693, 1800, 1813,
    1814, 1815, 1968, 1969, 1970, 1991, 2183, 2244, 2975, 3008, 3010, 3012, 3014, 3028, 3029,
    3030, 3032, 3035, 3036, 3037, 3040, 3072, 3852, 4232, 4236, 4240, 4244, 5466, 5467, 5468,
    5469, 5470, 5471, 5472, 5473, 5474, 5475, 5476, 5477, 5478, 5479, 5480, 5481, 5482, 5483,
    5484, 5485, 5487, 5488, 5489, 5490, 5491, 5492, 5493, 5494, 5495, 5496, 5497,
  ],
  food: [
    40, 63, 71, 125, 127, 513, 608, 614, 936, 949, 995, 1205, 1648, 1649, 2484, 2485, 2486,
    2493, 2495, 2504, 2505, 2558, 2569, 2570, 2571, 2572, 2573, 2574, 2575, 2613, 2614, 2627,
    2629, 2630, 2631, 2632, 2633, 2635, 2637, 2643, 2644, 2727, 2728, 2729, 2730, 2731, 2732,
    2733, 2734, 2735, 2736, 2737, 2738, 2739, 2740, 2741, 2833, 2925, 2926, 2927, 2928, 2929,
    2930, 2931, 2932, 2933, 2935, 2936, 2937, 2938, 2939, 2940, 2941, 4040, 4052, 4074, 4076,
    4944, 4955, 4956, 4978,
  ],
  arrow: [
    7, 96, 1146, 1147, 1816, 1818,
  ],
  gem: [
    236, 237, 238, 239, 240, 241, 242, 243, 245, 246, 247, 319, 330, 986, 994, 1026, 1027,
    1817, 2283, 2285, 2287, 2289, 2291, 2293, 2295, 2448, 2450, 2452, 2454, 2460, 2606, 2977,
    5514,
  ],
  coin: [
    5, 2434,
  ],
  quest: [
    17, 43, 56, 59, 91, 139, 314, 324, 437, 444, 465, 512, 591, 604, 671, 672, 865, 909, 912,
    914, 917, 918, 919, 920, 921, 922, 923, 924, 925, 926, 927, 934, 935, 938, 940, 944, 945,
    946, 952, 953, 958, 973, 987, 988, 990, 991, 992, 993, 997, 998, 999, 1000, 1002, 1004,
    1005, 1006, 1012, 1013, 1014, 1015, 1021, 1022, 1023, 1024, 1025, 1028, 1039, 1041, 1042,
    1043, 1045, 1046, 1048, 1049, 1135, 1138, 1164, 1176, 1318, 1320, 1322, 1342, 1343, 1395,
    1425, 1528, 1531, 1536, 1537, 1538, 1539, 1540, 1541, 1643, 1644, 1760, 1780, 1781, 1782,
    1786, 1867, 1868, 1869, 1870, 1922, 1924, 1926, 1928, 1930, 1931, 1932, 1933, 1934, 1935,
    1936, 1938, 1942, 1944, 1956, 1971, 1972, 1974, 1975, 1978, 1992, 1996, 1997, 1998, 1999,
    2001, 2018, 2045, 2051, 2052, 2053, 2054, 2055, 2056, 2057, 2058, 2059, 2060, 2061, 2062,
    2071, 2078, 2079, 2084, 2086, 2088, 2089, 2091, 2093, 2094, 2095, 2096, 2101, 2102, 2105,
    2107, 2108, 2109, 2110, 2111, 2112, 2117, 2118, 2119, 2120, 2122, 2123, 2124, 2125, 2126,
    2169, 2177, 2178, 2179, 2215, 2246, 2247, 2248, 2249, 2250, 2251, 2252, 2253, 2254, 2255,
    2256, 2257, 2258, 2323, 2324, 2325, 2326, 2327, 2328, 2329, 2372, 2373, 2374, 2375, 2376,
    2377, 2378, 2379, 2380, 2381, 2411, 2412, 2413, 2414, 2415, 2416, 2417, 2418, 2419, 2420,
    2421, 2422, 2423, 2424, 2425, 2426, 2427, 2435, 2436, 2437, 2438, 2440, 2441, 2442, 2443,
    2444, 2445, 2456, 2457, 2458, 2459, 2461, 2462, 2463, 2471, 2472, 2474, 2476, 2478, 2480,
    2481, 2482, 2483, 2498, 2518, 2553, 2556, 2557, 2560, 2598, 2987, 2992, 2993, 2994, 2995,
    2996, 2998, 3000, 3002, 3031, 3039, 3276, 3565,
  ],
};

/**
 * 바닥에 놓였을 때의 그래픽 번호(`grd_gfx_id`) → 종류 (자동 생성, 746개).
 *
 * 서버 `S_DropItem`은 인벤토리와 **다른 번호**를 보낸다
 * (jp.l1j.server.packets.server.S_DropItem → L1Item.getGroundGfxId → DB grd_gfx_id).
 * 두 번호는 같은 공간을 다르게 쓰기 때문에 위 표로 조회하면 엉뚱한 아이콘이 나온다 —
 * 실제로 1,012개의 grd_gfx_id 중 200개가 어떤 아이템의 inv_gfx_id와 값이 겹친다.
 */
const GROUND_ICON_GFX: Partial<Record<ItemIconKind, number[]>> = {
  sword: [
    3, 26, 59, 76, 82, 355, 356, 781, 890, 2923, 2924, 2925, 2926, 2927, 2928, 2929, 2930,
    2931, 2981, 3131, 3266, 3304, 3305, 3308, 3314, 3422, 3431, 3441, 3733, 3734, 4256, 4317,
    4352, 4353, 4362, 4759, 4763, 4764, 4954, 4955, 4975, 4978, 4979, 4981, 5002, 5004, 5316,
    5426, 6401, 6735, 6738, 6939, 6940, 6956, 6957, 6962, 6967, 7811, 7817, 7820, 8996, 9238,
    9239, 10218, 10341, 10342, 10864,
  ],
  dagger: [
    68, 354, 2908, 2909, 2910, 2911, 2912, 2913, 2914, 2915, 2916, 3309, 3370, 3433, 3850,
    4112, 4118, 4318, 4413, 4976, 4982, 5003, 5005, 5046, 5605, 6736, 7802, 8999, 10167,
  ],
  axe: [
    2, 14, 58, 60, 83, 358, 3287, 3307, 4110, 4257, 5059, 5493, 5606, 6008, 6734,
  ],
  spear: [
    5, 27, 71, 77, 887, 3306, 3432, 4119, 5504, 5607, 6739,
  ],
  bow: [
    12, 2918, 2919, 2920, 3313, 3423, 3424, 3436, 3735, 4316, 4761, 5055, 5402, 5601, 6959,
    7808, 8997,
  ],
  staff: [
    75, 773, 3262, 3264, 3265, 3310, 3428, 3709, 3791, 4255, 4962, 4980, 5006, 5404, 6737,
    6958, 6966, 7814, 7839, 8998, 10862,
  ],
  armor: [
    9, 24, 1504, 2896, 2933, 3167, 3168, 3169, 3170, 3171, 3172, 3253, 3259, 3263, 3293, 3316,
    3426, 3427, 3429, 3437, 3708, 3712, 3731, 3768, 3769, 3770, 3771, 3772, 3773, 3774, 3794,
    3797, 3830, 3834, 3838, 3840, 3842, 3848, 4113, 4267, 4346, 4411, 4956, 4963, 4970, 5016,
    5035, 5038, 5051, 5225, 5226, 5308, 6355, 6402, 7666, 7667, 7668, 7669,
  ],
  helm: [
    18, 67, 357, 462, 1503, 2474, 2866, 2894, 2935, 3255, 3256, 3257, 3258, 3261, 3372, 3513,
    3818, 4111, 4244, 4342, 4415, 4688, 4957, 4964, 5044, 5045, 5313, 5603, 5961, 6098, 6357,
    7519, 7520,
  ],
  shield: [
    23, 50, 359, 996, 3173, 3174, 3425, 4416, 4961, 4968, 5030, 6360, 6947, 6951, 6964, 7045,
    7046, 7620, 8815, 10282, 10394, 10395,
  ],
  boots: [
    25, 2872, 2895, 2936, 3254, 3260, 3298, 3317, 3430, 3711, 3804, 4242, 4270, 4344, 4412,
    4960, 4967, 4971, 5054, 5254, 5309, 6359, 9281,
  ],
  glove: [
    182, 2868, 2921, 2934, 3315, 3816, 3964, 4114, 4115, 4116, 4117, 4243, 4268, 4343, 4414,
    4765, 4959, 4966, 4972, 5024, 5041, 5058, 5403, 6358, 9282,
  ],
  cloak: [
    13, 1493, 1495, 2932, 2937, 2938, 2939, 2940, 2941, 3249, 3311, 3312, 3439, 3732, 3738,
    3806, 3808, 3810, 3812, 3814, 3822, 3824, 4269, 4314, 4345, 4958, 4965, 4969, 5023, 5037,
    5056, 5310, 6126, 6127, 6128, 6356, 7326, 7328, 9283,
  ],
  ring: [
    21, 2880, 3165, 3166, 3301, 3302, 3460, 3718, 3793, 3960, 3975, 4120, 4348, 4349, 4350,
    4351, 4687, 5039, 5040, 5060, 5255, 5315, 5785, 6285, 6313, 6361, 6363, 6954, 6960, 7069,
    7070, 7071, 7072, 7715, 10301, 10302, 10303, 10304, 10677,
  ],
  amulet: [
    8, 3130, 3158, 3159, 3160, 3161, 3162, 3163, 3164, 3303, 3707, 3792, 3796, 3976, 4347,
    4973, 4974, 5053, 5093, 5095, 5105, 5314, 5538, 5539, 5540, 5541, 5542, 5543, 5544, 5545,
    5598, 5599, 5675, 5964, 6362, 7521, 9284, 9285, 10674, 10675, 10676,
  ],
  belt: [
    1165, 3119, 3120, 3121, 3963, 4977, 6955,
  ],
  potion: [
    20, 41, 42, 43, 44, 45, 46, 47, 79, 81, 881, 1732, 1733, 2983, 2984, 2985, 2986, 2987,
    2988, 3037, 3038, 3039, 3040, 3041, 3042, 3043, 3461, 3462, 3681, 3682, 3683, 3684, 3714,
    3836, 4249, 4250, 4324, 4338, 5714, 5718, 5842, 5843, 5844, 5959, 5960, 5984, 5985, 5987,
    5988, 5990, 5992, 5993, 5999, 6000, 6121, 6124, 6287, 6288, 6683, 6948, 7735, 8703,
  ],
  scroll: [
    11, 22, 28, 151, 776, 777, 1561, 2876, 2878, 2879, 2956, 2957, 2958, 3177, 3716, 4282,
    4283, 4286, 4289, 5399, 5612, 6929, 6930, 6931, 6932, 6933, 6935, 6936, 6938, 6943, 6944,
    6945, 6950,
  ],
  food: [
    36, 69, 72, 73, 74, 80, 1490, 2058, 2475, 2723, 3176, 4251, 4377, 5839, 5981, 5982, 5983,
    5989, 5991, 6001, 6123, 6164, 6165, 6166, 6167, 6168, 6169, 6170, 6365, 6366, 6367, 6368,
    6369, 6370, 6371, 6372, 6373, 6374, 6535, 6536, 6537, 6538, 6539, 6540, 6541, 6542, 6543,
    6544, 6545, 6546, 6547, 6548, 6549, 6550, 8590, 8591, 10017, 10026, 10027, 10210,
  ],
  arrow: [
    66, 2979, 2980, 4451, 4453,
  ],
  gem: [
    771, 772, 774, 775, 4452, 6330, 6455,
  ],
  coin: [
    7,
  ],
  quest: [
    19, 150, 884, 886, 906, 1166, 1215, 1489, 2152, 2153, 2867, 2869, 2870, 2871, 2873, 2874,
    2875, 2877, 2982, 3122, 3129, 3328, 3371, 3373, 3375, 3458, 3459, 3526, 3721, 3820, 3832,
    3852, 3853, 3854, 3855, 3856, 3857, 3965, 3966, 4301, 4337, 4354, 4355, 4356, 4755, 4756,
    4757, 4758, 4760, 4983, 4984, 4985, 4986, 4987, 4989, 4990, 4991, 4992, 4993, 4995, 4997,
    4999, 5001, 5017, 5025, 5026, 5027, 5028, 5029, 5031, 5032, 5036, 5042, 5043, 5047, 5048,
    5049, 5050, 5052, 5070, 5119, 5122, 5123, 5165, 5167, 5169, 5170, 5172, 5183, 5208, 5210,
    5211, 5218, 5219, 5220, 5221, 5235, 5236, 5237, 5238, 5240, 5241, 5242, 5243, 5248, 5419,
    5491, 5613, 5614, 5827, 5828, 5864, 5921, 5922, 5923, 5924, 5925, 5927, 5928, 5929, 5930,
    5994, 6122, 6125, 6375, 6934, 6937, 6949, 6961, 6963, 7088,
  ],
};

/** 위 표를 뒤집은 조회용 Map. 모듈 로드 시 한 번만 만든다. */
const GFX_TO_KIND: Map<number, ItemIconKind> = (() => {
  const map = new Map<number, ItemIconKind>();
  (Object.keys(ICON_GFX) as ItemIconKind[]).forEach((kind) => {
    for (const gfx of ICON_GFX[kind] ?? []) map.set(gfx, kind);
  });
  return map;
})();

const GROUND_GFX_TO_KIND: Map<number, ItemIconKind> = (() => {
  const map = new Map<number, ItemIconKind>();
  (Object.keys(GROUND_ICON_GFX) as ItemIconKind[]).forEach((kind) => {
    for (const gfx of GROUND_ICON_GFX[kind] ?? []) map.set(gfx, kind);
  });
  return map;
})();

/**
 * 이름으로 건지는 보조 규칙.
 *
 * DB에 없는 gfxId(신규 아이템, 서버 커스텀)라도 표시명으로 대충 갈래를 잡는다.
 * gfxId 매핑이 우선이고 여기는 폴백 직전 단계다.
 */
const NAME_RULES: Array<[ItemIconKind, string[]]> = [
  ['coin', ['아데나', 'アデナ', 'adena']],
  ['potion', ['물약', 'ポーション', 'potion']],
  ['scroll', ['주문서', 'スクロール', 'scroll']],
  ['arrow', ['화살', '矢', 'arrow']],
  ['gem', ['보석', '宝石', 'gem']],
  ['food', ['빵', '고기', 'パン', 'food']],
];

/**
 * gfxId(+ 표시명)로 아이콘 종류를 정한다.
 * 매핑이 없으면 이름 규칙을 보고, 그래도 모르면 'misc'.
 */
export function itemIconKind(gfxId: number, name?: string | null): ItemIconKind {
  const byGfx = GFX_TO_KIND.get(gfxId);
  if (byGfx) return byGfx;

  const lower = (name ?? '').toLowerCase();
  if (lower) {
    for (const [kind, words] of NAME_RULES) {
      if (words.some((w) => lower.includes(w.toLowerCase()))) return kind;
    }
  }
  return ITEM_ICON_FALLBACK;
}

/**
 * 바닥에 놓인 아이템(S_DropItem)의 아이콘 종류.
 *
 * `itemIconKind`와 갈라 두는 이유는 서버가 보내는 번호가 다르기 때문이다
 * (`grd_gfx_id` vs `inv_gfx_id`). 인벤토리 쪽 표로 조회하면 200개가 값만 겹쳐
 * 엉뚱한 그림이 나온다. 표에 없으면 이름 규칙 → 'misc' 순으로 떨어진다.
 */
export function groundItemIconKind(gfxId: number, name?: string | null): ItemIconKind {
  const byGfx = GROUND_GFX_TO_KIND.get(gfxId);
  if (byGfx) return byGfx;

  const lower = (name ?? '').toLowerCase();
  if (lower) {
    for (const [kind, words] of NAME_RULES) {
      if (words.some((w) => lower.includes(w.toLowerCase()))) return kind;
    }
  }
  return ITEM_ICON_FALLBACK;
}

/** 아이콘 SVG의 URL 경로. */
export function itemIconPath(kind: ItemIconKind): string {
  return `/assets/gfx/items/${kind}.svg`;
}

/** 아이콘의 Phaser 텍스처 키. 같은 종류면 항상 같은 키다. */
export function itemIconTextureKey(kind: ItemIconKind): string {
  return `item|${kind}`;
}

/**
 * 아이콘 SVG를 Phaser 텍스처로 등록한다.
 * @returns 등록된 텍스처 키.
 */
export function loadItemIcon(scene: Phaser.Scene, kind: ItemIconKind): Promise<string> {
  return loadSvgAsTexture(scene, itemIconTextureKey(kind), itemIconPath(kind));
}

/**
 * 전 종류를 미리 받아둔다. 인벤토리를 처음 열 때 한 칸씩 늦게 뜨는 것을 막는다.
 * 개별 실패는 삼킨다 — 슬롯이 '*' 텍스트로 폴백하므로 화면이 비지는 않는다.
 *
 * @returns 실제로 준비된 아이콘 수.
 */
export async function preloadItemIcons(scene: Phaser.Scene): Promise<number> {
  const results = await Promise.all(
    ITEM_ICON_KINDS.map((kind) => loadItemIcon(scene, kind).catch(() => null))
  );
  return results.filter((key) => key !== null).length;
}
